/* eslint-disable no-console, no-param-reassign */

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getSeo } from 'services/requests'
import { useParams } from 'react-router-dom'
import { MetadataForm } from 'components/organisms'
import { FacebookShare } from 'components/molecules'
import { Spacer } from 'components/atoms'
import { useDebounce } from 'react-use'
import styled, { withTheme } from 'styled-components'
import ContentLoader from 'react-content-loader';
import { toast } from 'react-toastify';
import { Toast } from 'containers';

const Loader = withTheme(({ theme }) => (
  <LoaderWrapper>
    <ContentLoader
      height={56}
      width={200}
      speed={1}
      primaryColor={theme.colors.bgAnimation}
      secondaryColor={theme.colors.bgLighterS}
      title="a carregar histórico..."
    >
      <rect width="200" height="24" />
      <rect y="36" width="140" height="24" />
    </ContentLoader>
  </LoaderWrapper>
));


const SeoEditor = ({
  initialProps, onChange
}) => {
  const { subweb, pageId, lang } = useParams()
  const [dynamicSeo, setDynamicSeo] = useState(null)
  const [isLoading, setLoading] = useState(false)
  const [metadata, setMetadata] = useState(initialProps)
  const handleChange = (newProps) => {
    const mergedProps = { ...initialProps, ...newProps }
    setMetadata(mergedProps)
  }

  const initialPropsComputed = useMemo(() => {
    if (dynamicSeo && dynamicSeo.image && typeof dynamicSeo.image === 'string') {
      return {
        ...initialProps,
        image: dynamicSeo.image,
      }
    }
    return initialProps
  }, [initialProps, dynamicSeo])

  // fetch dynamic seo if no image is present
  // for legacy pages only, now all pages are created with a default image
  useEffect(() => {
    const shouldGetSeo = initialPropsComputed.image === null
      || initialPropsComputed.image === undefined
    async function fetchSeo() {
      setLoading(true)
      try {
        const { data } = await getSeo({ pageId, subweb, lang })
        setDynamicSeo(data)
        setLoading(false)
      } catch (err) {
        toast.error(
          <Toast
            type="error"
            title="Erro a obter metadados da página."
            description={err.message}
          />
        );
        setLoading(false)
      }
    }
    if (shouldGetSeo && subweb && pageId) {
      fetchSeo()
    }
  }, [initialPropsComputed, subweb, pageId, lang])

  // update metadata for facebook share preview when initialProps change
  useEffect(() => {
    setMetadata(initialPropsComputed)
  }, [initialPropsComputed])

  useDebounce(() => {
    onChange(metadata);
  }, 200,
  [metadata]
  )

  if (isLoading) {
    return (
      <>
        <FacebookShare
          withDescription
          url="ua.pt"
          isLoading
        />
        <Spacer bottom="xl" />
        <Loader />
      </>
    )
  }

  return (
    <>
      <FacebookShare
        title={metadata && metadata.title ? metadata.title : ''}
        description={metadata && metadata.description ? metadata.description : ''}
        src={metadata && metadata.image ? metadata.image : null}
        withDescription
        url="ua.pt"
      />
      <Spacer bottom="xl" />
      <MetadataForm
        initialProps={initialPropsComputed}
        onChange={handleChange}
      />
    </>
  );
};

SeoEditor.propTypes = {
  onChange: PropTypes.func,
  initialProps: PropTypes.shape({
    image: PropTypes.shape({
      url: PropTypes.string
    })
  }).isRequired,
};

SeoEditor.defaultProps = {
  onChange: () => null,
};

const LoaderWrapper = styled.div`
  width: 100%;
  height: 56px;
  svg {
    width: 100%;
    height: 100%;
  }
`;

export default SeoEditor;
